import type { CapabilityEntry } from "../lib/api";
import { ErrorState } from "../shell/ui";
import { LightMotion } from "../components/LightMotion";
import { RadioGroup } from "../components/rumonai";
import { NAMELESS_MODEL } from "./api";

/**
 * What a model reads, in the owner's words, from the flags its plugin registered it with.
 *
 * Nothing is said for a model that registered none of them: a line here is a claim about what the Agent
 * can hand the model, and an unregistered flag is not a reason to claim "only text".
 */
export function whatItTakes(model: CapabilityEntry): string {
  const spec = model.spec ?? {};
  if (!("supports_vision" in spec) && !("supports_video" in spec)) return "";
  const metadata = (spec.metadata && typeof spec.metadata === "object" ? spec.metadata : {}) as Record<string, unknown>;
  const kinds = [spec.supports_vision && "图片", metadata.supports_audio && "音频", spec.supports_video && "视频"]
    .filter((kind): kind is string => typeof kind === "string");
  if (!kinds.length) return "只读文字";
  const all = ["文字", ...kinds];
  return `能读${all.slice(0, -1).join("、")}和${all[all.length - 1]}`;
}

/**
 * The models an Agent can answer with, as a calm list: each by its name and one plain line of what it
 * reads, the one in use marked in place. Choosing is one click and nothing else to press; the row that was
 * clicked says it is switching until the choice is read back, and an error is said under the list, where
 * the owner acted.
 */
export function ModelChoice({ models, names, chosen, failover, switching, error, onChoose, onRetry }: {
  models: CapabilityEntry[];
  names: Map<string, string>;
  /** The key of the model in use (chosen, or standing in while none is), or "" when there is none. */
  chosen: string;
  /** One clause: what runs when the chosen one fails. */
  failover: string;
  /** The key being switched to, until the Agent answers with it. */
  switching: string | null;
  error?: unknown;
  onChoose: (key: string) => void;
  onRetry: () => void;
}) {
  return <fieldset className="model-choice" aria-busy={switching !== null}>
    <legend>对话用的模型</legend>
    <RadioGroup label="对话用的模型" name="chat-model" value={chosen} disabled={switching !== null}
      onValueChange={onChoose} className="model-choice-list" items={models.map(model => {
      const on = model.key === chosen;
      const takes = whatItTakes(model);
      return { value: model.key, label: <>
        <span className="model-choice-title">{names.get(model.key) ?? NAMELESS_MODEL}</span>
        {takes ? <span className="model-choice-takes">{takes}</span> : null}
        {switching === model.key
          ? <span className="model-choice-state" role="status"><LightMotion compact state="working" />正在换…</span>
          : on ? <span className="model-choice-state model-choice-mark pa-reveal">当前使用</span> : null}
      </> };
    })} />
    {failover ? <p className="model-choice-failover">{failover}</p> : null}
    {error ? <ErrorState error={error} onRetry={onRetry} /> : null}
  </fieldset>;
}
